import React, { useEffect, useState } from 'react';
import Rodal from '../../../../components/Rodal/Rodal';
import { ModalBody, ModalHeader } from 'reactstrap';
import './ValueRangeModal.scss';
import { CurrencyInput, Form } from '@availity/form';
import { Row, Col } from 'reactstrap';
import * as yup from 'yup';
import { useStateSelector } from '../../../../store/selectors';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import createNotification from '../../../../utils/createNotification';
import { isDfmGatedRangeEvent } from '../../../ConnectMarketplaceDashboard/EventActionAvailabilityHelper';

interface ValueRangeModalProps {
    isRangeOfValueVisible: boolean;
    onClose: () => void;
    onRangeOfValueConfirm: (eventId: string, min: number, max: number) => void;
    selectedEventId?: string;
    selectedActionId?: number;
    isRangeOfValueReadonly?: boolean;
    minValue?: number;
    maxValue?: number;
    isPreviewModeActive?: boolean;
    isConfirmationInProgress?: boolean;
}

const ValueRangeModal = (props: ValueRangeModalProps) => {
    const selectedOpportunity = useStateSelector(
        (state) => state.opportunities.selectedOpportuity
    );

    // #943: updated-range events (positioned after the DFM event) accept submissions only from
    // buyers who purchased the DFM. Everyone else keeps the button but gets this message instead
    // of the form; the server rejects direct submissions as well.
    const selectedEvent = selectedOpportunity?.events?.find(
        (e) => e.id === props.selectedEventId
    );
    const isDfmPurchaseRequired =
        !props.isPreviewModeActive &&
        selectedEvent != null &&
        isDfmGatedRangeEvent(selectedEvent, selectedOpportunity?.events ?? []) &&
        !selectedOpportunity?.buyer?.hasPurchasedDfm;

    const validationSchema = yup.object().shape({
        minimum: yup
            .number()
            .required('Minimum value is required')
            .moreThan(0, 'Minimum must be greater than 0'),
        maximum: yup
            .number()
            .required('Maximum value is required')
            .moreThan(0, 'Maximum must be greater than 0')
            .moreThan(
                yup.ref('minimum'),
                'Maximum has to be bigger than Minimum'
            ),
    });

    const [valueOfRangeMin, setValueOfRangeMin] = useState(
        props.isRangeOfValueReadonly ? props.minValue.toString() ?? '0' : '0'
    );
    const [valueOfRangeMax, setValueOfRangeMax] = useState(
        props.isRangeOfValueReadonly ? props.maxValue.toString() ?? '0' : '0'
    );

    const [headerText, setHeaderText] = useState(
        props.isPreviewModeActive
            ? 'Indicative Range of Value'
            : 'Preliminary Indicative Range of Value'
    );
    const [bodyText, setBodyText] = useState(
        'Please provide your indicative range of enterprise value for this opportunity. This valuation indication will be presented to the seller.'
    );
    const [buttonText, setButtonText] = useState('Confirm Range of Value');

    const [isConfirmationPopupVisible, setIsConfirmationPopupVisible] =
        useState(false);

    const [initialFormValues, setInitialFormValues] = useState({
        minimum: valueOfRangeMin,
        maximum: valueOfRangeMax,
    });
    const [isFormLoading, setIsFormLoading] = useState(true);

    useEffect(() => {
        if (!props.isPreviewModeActive) {
            setInitialValueRangeFromPreviousExpressions();
        } else {
            setIsFormLoading(false);
        }
    }, []);

    const setInitialValueRangeFromPreviousExpressions = () => {
        const expressionsWithRange =
            selectedOpportunity?.buyer?.actionState
                .filter((item) => item.minPrice !== null && item.maxPrice !== null)
                .sort((a, b) => a.actionId - b.actionId);
        const expressionsWithRangeExcludingCurrent = expressionsWithRange
            ?.filter((item) => item.actionId !== props.selectedActionId)
            .reverse();
        const previousValueOfRangeMin =
            expressionsWithRangeExcludingCurrent?.[0]?.minPrice?.toString();
        const previousValueOfRangeMax =
            expressionsWithRangeExcludingCurrent?.[0]?.maxPrice?.toString();

        if (!props.isRangeOfValueReadonly) {
            const updatedMin = previousValueOfRangeMin ?? '0';
            const updatedMax = previousValueOfRangeMax ?? '0';
            setValueOfRangeMin(updatedMin);
            setValueOfRangeMax(updatedMax);
            setInitialFormValues({
                minimum: updatedMin,
                maximum: updatedMax,
            });
        }
        const firstValueRange = expressionsWithRange[0] ?? null;
        if (
            expressionsWithRangeExcludingCurrent.length > 0 &&
            firstValueRange?.actionId !== props.selectedActionId
        ) {
            setHeaderText('Updated Indicative Range of Value');
            setBodyText(
                'Please update your preliminary indicative range of value for this opportunity. This updated valuation indication will be presented to the seller.'
            );
            setButtonText('Confirm Updated Range of Value');
        }
        setIsFormLoading(false);
    };

    const handleRangeOfValueConfirm = (
        values: { minimum: string; maximum: string },
        resetForm: (values: {
            values: { minimum: string; maximum: string };
        }) => void
    ) => {
        if (props.selectedEventId) {
            props.onRangeOfValueConfirm(
                props.selectedEventId,
                Number(values.minimum),
                Number(values.maximum)
            );
        }
        resetForm({
            values: initialFormValues,
        });
    };

    return (
        <>
            <Rodal
                width={550}
                height={500}
                visible={props.isRangeOfValueVisible}
                animation={'slideRight'}
                showCloseButton={true}
                onClose={props.onClose}
                showMask={false}
                className={'range-of-value-modal'}>
                <ModalHeader>
                    {isDfmPurchaseRequired
                        ? 'Updated Indicative Range of Value'
                        : headerText}
                </ModalHeader>
                {isDfmPurchaseRequired ? (
                    <ModalBody>
                        <div className="range-of-value-text-container">
                            <span className="range-of-value-text">
                                {
                                    'To provide an updated indicative range of value, you first need to purchase the Detailed Financial Model. It is available in the Documents tab.'
                                }
                            </span>
                        </div>
                        <div className="range-of-value-buttons">
                            <button
                                type="button"
                                className="btn btn-cancel"
                                onClick={() => {
                                    props.onClose();
                                }}>
                                {'Close'}
                            </button>
                        </div>
                    </ModalBody>
                ) : (
                <ModalBody>
                    {!props.isPreviewModeActive && (
                        <div className="range-of-value-text-container">
                            <span className="range-of-value-text">
                                {bodyText}
                            </span>
                        </div>
                    )}
                    <div className="range-of-value-label-container">
                        <span className="range-of-value-label">
                            {'Range of Value:'}
                        </span>
                    </div>

                    <div className="range-of-value-form">
                        {isFormLoading ? (
                            <></>
                        ) : (
                            <Form
                                initialValues={initialFormValues}
                                validationSchema={validationSchema}
                                onSubmit={() => {}}>
                                <Row>
                                    <Col md={6}>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="mr-2 range-label">
                                                {'Minimum: '}
                                            </div>
                                            <CurrencyInput
                                                name={'minimum'}
                                                disabled={
                                                    props.isRangeOfValueReadonly
                                                }
                                                value={valueOfRangeMin.toString()}
                                                allowDecimals={false}
                                                allowNegativeValue={false}
                                                prefix="$"
                                                onValueChanged={(value) => {
                                                    const newValue = value
                                                        ? value.replace(
                                                              '.00',
                                                              ''
                                                          )
                                                        : '0';

                                                    setValueOfRangeMin(
                                                        newValue
                                                    );
                                                }}
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="mr-2 range-label">
                                                {'Maximum: '}
                                            </div>
                                            <CurrencyInput
                                                name={'maximum'}
                                                disabled={
                                                    props.isRangeOfValueReadonly
                                                }
                                                value={valueOfRangeMax.toString()}
                                                allowDecimals={false}
                                                allowNegativeValue={false}
                                                prefix="$"
                                                onValueChanged={(value) => {
                                                    const newValue = value
                                                        ? value.replace(
                                                              '.00',
                                                              ''
                                                          )
                                                        : '0';

                                                    setValueOfRangeMax(
                                                        newValue
                                                    );
                                                }}
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <div className="range-of-value-buttons">
                                    <button
                                        type="button"
                                        className="btn btn-cancel"
                                        onClick={() => {
                                            props.onClose();
                                        }}>
                                        {'Close'}
                                    </button>
                                    {!props.isPreviewModeActive && (
                                        <button
                                            disabled={
                                                props.isRangeOfValueReadonly
                                            }
                                            type="submit"
                                            onClick={async () => {
                                                const isValid =
                                                    await validationSchema.isValid(
                                                        {
                                                            minimum:
                                                                valueOfRangeMin,
                                                            maximum:
                                                                valueOfRangeMax,
                                                        }
                                                    );

                                                if (isValid) {
                                                    setIsConfirmationPopupVisible(
                                                        true
                                                    );
                                                }
                                            }}
                                            className="btn btn-primary">
                                            {buttonText}
                                        </button>
                                    )}
                                </div>
                            </Form>
                        )}
                    </div>
                </ModalBody>
                )}
            </Rodal>
            {isConfirmationPopupVisible && (
                <ConfirmationModal
                    header={buttonText}
                    isVisible={isConfirmationPopupVisible}
                    setIsVisible={setIsConfirmationPopupVisible}
                    confirmationText="Are you sure you want to confirm this indicative range of value for the opportunity?"
                    params={''}
                    onConfirm={() => {
                        handleRangeOfValueConfirm(
                            {
                                minimum: valueOfRangeMin,
                                maximum: valueOfRangeMax,
                            },
                            () => {}
                        );
                    }}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={props.isConfirmationInProgress}
                />
            )}
        </>
    );
};

export default ValueRangeModal;
